import { Button, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIndividualPaymentFilters } from '../../../hooks/useIndividualPaymentFilters';
import { usePaymentTypesDropdown } from '../../../hooks/usePaymentTypesDropdown';
import type { AdvancedFilterKey } from '../../../types/individualPayment';
import { studentFiltersFromSelection } from '../../../utils/studentSelection';
import {
  ClassStudentAutocomplete,
  CompanyAutocomplete,
  StudentAutocomplete,
} from '../LookupAutocomplete';

export interface IndividualPaymentFilterBarProps {
  /** Indica busca em andamento — desabilita e mostra spinner em "Pesquisar". */
  readonly isLoading?: boolean;
  /** Chamado ao aplicar os filtros, para a página voltar à primeira. */
  readonly onSearch?: () => void;
}

/**
 * Campos de texto do painel avançado, na ordem exibida (grid de 3 colunas).
 *
 * `as const` preserva as chaves como literais para que `t()` valide cada uma
 * contra o JSON `pt-BR` em tempo de compilação.
 */
const ADVANCED_FIELDS = [
  { key: 'ra', labelKey: 'filters.ra', placeholderKey: 'filters.raPlaceholder' },
  { key: 'cpf', labelKey: 'filters.cpf', placeholderKey: 'filters.cpfPlaceholder' },
  { key: 'email', labelKey: 'filters.email', placeholderKey: 'filters.emailPlaceholder' },
  {
    key: 'thirdPartyCpf',
    labelKey: 'filters.thirdPartyCpf',
    placeholderKey: 'filters.thirdPartyCpfPlaceholder',
  },
  {
    key: 'thirdPartyCnpj',
    labelKey: 'filters.thirdPartyCnpj',
    placeholderKey: 'filters.thirdPartyCnpjPlaceholder',
  },
  {
    key: 'ourNumber',
    labelKey: 'filters.ourNumber',
    placeholderKey: 'filters.ourNumberPlaceholder',
  },
  {
    key: 'invoiceNumber',
    labelKey: 'filters.invoiceNumber',
    placeholderKey: 'filters.invoiceNumberPlaceholder',
  },
] as const satisfies readonly {
  readonly key: AdvancedFilterKey;
  readonly labelKey: string;
  readonly placeholderKey: string;
}[];

/**
 * Barra de filtros da pesquisa de Pagamento Individual.
 *
 * Linha 1: autocomplete de aluno + "Pesquisar" + toggle do painel avançado.
 * Linha 2: autocompletes de empresa e turma, select de método de pagamento e o
 * chip de limpar. Painel avançado: os 7 campos de texto.
 *
 * A busca só dispara em "Pesquisar" — digitar nos campos apenas atualiza o
 * rascunho em `useIndividualPaymentFilters`.
 */
export function IndividualPaymentFilterBar({
  isLoading = false,
  onSearch,
}: IndividualPaymentFilterBarProps) {
  const { t } = useTranslation('individualPayment');
  const {
    filters,
    updateFilters,
    applyFilters,
    resetFilters,
    resetAdvancedFilters,
    canApply,
    advancedActiveCount,
  } = useIndividualPaymentFilters();
  const { paymentTypes } = usePaymentTypesDropdown();
  const [showAdvanced, setShowAdvanced] = useState(false);
  // O `Autocomplete` do ADS guarda o texto exibido em estado interno (`query`),
  // ressincronizado a partir de `selectedItem.label` só quando há seleção — e os
  // wrappers de lookup guardam o termo de busca em `useState` local. Nenhum dos
  // dois reage ao reset dos átomos, então limpar pelos filtros deixaria o texto
  // antigo visível no campo. Trocar esta chave remonta os três lookups,
  // zerando ambos os estados de uma vez.
  const [lookupResetNonce, setLookupResetNonce] = useState(0);

  const hasActiveFilters = canApply;

  const handleSearch = () => {
    applyFilters();
    onSearch?.();
  };

  const handleClearAll = () => {
    resetFilters();
    setLookupResetNonce(nonce => nonce + 1);
  };

  return (
    <div className="flex flex-col">
      {/* Linha 1: busca por aluno + ações */}
      <div className="flex items-end justify-between gap-nano px-xs pt-xs pb-mini">
        <div className="w-full">
          {/*
            A seleção vira três filtros: id (dígitos da chave `"USR260953"`),
            nome e e-mail (extraídos do rótulo `"Nome  [email]"`). Digitar sem
            selecionar também filtra, aí só pelo nome.
          */}
          <StudentAutocomplete
            key={lookupResetNonce}
            label={t('filters.student')}
            placeholder={t('filters.studentPlaceholder')}
            value={filters.studentLabel}
            selectedLabel={filters.studentLabel}
            onChange={(key, itemLabel) =>
              updateFilters(studentFiltersFromSelection(key, itemLabel))
            }
            onFreeTextChange={term =>
              updateFilters({
                personId: '',
                studentLabel: '',
                studentName: term,
                studentEmail: '',
              })
            }
          />
        </div>

        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={MagnifyingGlassIcon}
          disabled={!canApply || isLoading}
          isLoading={isLoading}
          onClick={handleSearch}
        >
          {t('filters.search')}
        </Button>

        <div className="relative shrink-0">
          <Button
            hierarchy={showAdvanced ? 'primary' : 'secondary'}
            size="md"
            leadingIcon={AdjustmentsHorizontalIcon}
            trailingIcon={showAdvanced ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => setShowAdvanced(prev => !prev)}
          >
            {t('filters.advanced')}
          </Button>

          {advancedActiveCount > 0 && !showAdvanced && (
            <span className="absolute -right-[0.375rem] -top-[0.375rem] flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-pill bg-ctx-interactive-primary ts-caption-xxxs !font-medium text-base-neutral-white">
              {advancedActiveCount}
            </span>
          )}
        </div>
      </div>

      {/* Linha 2: empresa, turma, método de pagamento */}
      <div className="flex flex-wrap items-end gap-nano px-xs pb-xs">
        <div className="min-w-[16rem] flex-1">
          <CompanyAutocomplete
            key={lookupResetNonce}
            label={t('filters.company')}
            placeholder={t('filters.companyPlaceholder')}
            value={filters.companyId}
            selectedLabel={filters.companyLabel}
            onChange={(companyId, companyLabel) => updateFilters({ companyId, companyLabel })}
          />
        </div>

        <div className="min-w-[16rem] flex-1">
          <ClassStudentAutocomplete
            key={lookupResetNonce}
            label={t('filters.classStudent')}
            placeholder={t('filters.classStudentPlaceholder')}
            value={filters.classStudentId}
            selectedLabel={filters.classStudentLabel}
            onChange={(classStudentId, classStudentLabel) =>
              updateFilters({ classStudentId, classStudentLabel })
            }
          />
        </div>

        <div className="min-w-[16rem] flex-1">
          <SelectInput
            label={t('filters.paymentMethod')}
            size="md"
            fullWidth
            value={filters.paymentMethod || 'all'}
            onValueChange={value => updateFilters({ paymentMethod: value === 'all' ? '' : value })}
          >
            <SelectInputItem custom={{ root: 'text-nowrap' }} value="all">
              {t('filters.all')}
            </SelectInputItem>
            {paymentTypes.map(type => (
              <SelectInputItem
                custom={{ root: 'text-nowrap' }}
                key={type.id}
                value={String(type.id)}
              >
                {type.label}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearAll}
            className="inline-flex items-center gap-quark rounded-pill border-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-quark ts-caption-xxs text-ctx-content-placeholder transition-colors hover:bg-ctx-interactive-secondary hover:text-ctx-content-base"
          >
            <XMarkIcon className="h-[0.8125rem] w-[0.8125rem]" />
            {t('filters.clear')}
          </button>
        )}
      </div>

      {/* Painel avançado */}
      {showAdvanced && (
        <div className="border-t-hairline border-ctx-layout-border">
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-nano">
            <div className="flex items-center gap-xxs">
              <AdjustmentsHorizontalIcon className="h-mini w-mini text-ctx-content-placeholder" />
              <span className="ts-caption-xxs !font-medium uppercase tracking-widest text-ctx-content-placeholder">
                {t('filters.advanced')}
              </span>
              {advancedActiveCount > 0 && (
                <span className="rounded-pill bg-ctx-interactive-primary px-xxs py-[0.0625rem] ts-caption-xxxs !font-medium text-base-neutral-white">
                  {t('filters.advancedActive', { count: advancedActiveCount })}
                </span>
              )}
            </div>

            {advancedActiveCount > 0 && (
              <button
                type="button"
                onClick={resetAdvancedFilters}
                className="inline-flex items-center gap-quark ts-caption-xxs text-ctx-content-placeholder transition-colors hover:text-ctx-content-base"
              >
                <XMarkIcon className="h-[0.8125rem] w-[0.8125rem]" />
                {t('filters.clearAdvanced')}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-xs p-xs md:grid-cols-2 lg:grid-cols-3">
            {ADVANCED_FIELDS.map(field => (
              <TextField
                fullWidth
                key={field.key}
                label={t(field.labelKey)}
                placeholder={t(field.placeholderKey)}
                size="md"
                value={filters[field.key]}
                onChange={event => updateFilters({ [field.key]: event.target.value })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
